// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { removeTab } from 'wdna-components/lib/tabs';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { config } from '../../../../../config/config';
import { lov, LovDO } from '../../../../configuration/requests';
import { company, CompanyDO } from '../../../requests';

import Form from './Form';

interface Props {
  rowId?: number;
  notify?: () => void;
}

interface State {
  loading?: boolean;
  update?: string;

  isFile?: boolean;
  modalShow?: boolean;
  modalData?: any;
  id?: number;
  name?: string;
  typeCompanyArray?: LovDO[];

  data?: Partial<CompanyDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private fileId: any;
  private fileName: any;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.state = {
      loading: false,

      isFile: false,
      modalShow: false,
      modalData: {},

      typeCompanyArray: [],

      data: {},
    };

    this.defaultValues = {};
    this.fileId = window.getStr();
    this.fileName = window.getStr();
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public load = async (): Promise<void> => {
    const { rowId } = this.props || {};
    const isNew: boolean = (rowId === undefined || !!isNaN(rowId));

    this.loadingStart();
    try {
      const [typeCompanyArray] = await Promise.all([
        lov<LovDO[]>('statics', 'type_company')
      ]);

      let rowData: Partial<CompanyDO> = this.state.data || {};
      if (!isNew) {
        rowData = await company<Partial<CompanyDO>>('get', String(rowId)) || {};
      }
      this.setState(
        {
          data: { ...rowData, },
          typeCompanyArray,
        },
        () => {
          this.defaultValues = _.cloneDeep(rowData);
        }
      );
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  }

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  }

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  }

  public onReset = (): void => {
    this.setState({
      data: {
        ...this.defaultValues,
      }
    });
  }

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { value, name } = e.target;
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        [name]: value,
      },
    }));
  }

  public handleUploadFile = (): void => {
    const { id, data } = this.state;

    const { fileId } = this;
    const file: any[] = $(`#${fileId}`).prop('files');
    if (!file[0]) {
      if (isNaN(Number(id))) {
        messageError('Error', 'Please select a valid file');
        return;
      }

      const formData: FormData = new FormData();
      formData.append('ufile', file[0]);

      if (data?.id !== undefined && !isNaN(data.id)) {
        formData.append('id', String(data.id));
      }

      const options: any = { headers: { 'Content-Type': 'multipart/form-data' } };

      this.loadingStart();
      company('dashboard', undefined, formData, options)
        ?.then(() => {
          messageInfo('Success', `Dashboard successfully ${data?.id ? 'edited' : 'created'} ${data?.name}`, 5000);
          const { notify } = this.props || {};
          if (typeof notify === 'function') {
            notify();
          }
          window.store.dispatch(removeTab('CE'));
        })
        .catch((error: any) => {
          this.uploadingError(error);
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  }

  public handleDeletedFile = (): void => {
    const { data } = this.state;

    messageBoxWarning('Delete', 'Do you want to delete this register ?', () => {
      const formData: FormData = new FormData();
      formData.append('id', String(data?.id));

      const options: any = { headers: { 'Content-Type': 'multipart/form-data' } };

      this.loadingStart();
      company('dashboard', undefined, formData, options)
        ?.then(() => {
          messageInfo('Success', 'Dashboard successfully deleted', 5000);
          const { notify } = this.props || {};
          if (typeof notify === 'function') {
            notify();
          }
          window.store.dispatch(removeTab('CE'));
        })
        .catch((error: any) => {
          this.uploadingError(error);
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  }

  public uploadingError = (error: any): void => {
    if (error) {
      messageError('Error', getErrorMessage(error));
    } else {
      messageError('Error', 'Unknown error');
    }
  };

  public onInfo = (value: any): void => {
    this.setState({
      update: window.getStr(),
      modalShow: true,
      modalData: value,
    });
    const { rowId } = this.props || {};
    const { protocol } = location;
    const host: string = protocol.concat('//', window.location.hostname);
    const url: string = host.concat(`/dashboard/company_${rowId || ''}.json`);
    this.chkFileOnURL(url).catch(() => { /* pass */ });
  };

  public onCloseModal = (): void => {
    this.setState({
      update: window.getStr(),
      modalShow: false,
    });
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): any => {
    e.preventDefault();
    const { data } = this.state;

    this.loadingStart();
    company('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Company successfully ${data?.id ? 'edited' : 'created'} ${data?.name}`, 5000);
        const { notify } = this.props || {};
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('CoE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public changeFileName = (): void => {
    const { fileId, fileName } = this;
    const file: any[] = $(`#${fileId}`).prop('files');
    if (!file[0]) {
      return;
    }

    const { name } = file[0];
    $(`#${fileName}`).val(name);
  };

  public handleChangeTwo = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.changeFileName();
    const { name, value, tagName } = e.target;

    if (tagName === 'SELECT') {
      const [outerName, innerName] = name.split('.');
      this.setState({
        [outerName]: {
          [innerName]: value,
        },
      });
    } else {
      this.setState({
        [name]: value,
      });
    }
  };

  public chkFileOnURL = async (url: string): Promise<boolean> => {
    const result: boolean = await fetch(url, { method: 'HEAD', mode: 'cors' })
      .then((resp: Response) => { return resp.ok; }, () => { return false; })
      .catch(() => { return false; });
    this.setState({
      isFile: result,
    });

    return result;
  }

  public download = (): void => {
    const { rowId } = this.props || {};
    const { isFile } = this.state;
    const { protocol } = location;
    const host: string = protocol.concat('//', window.location.hostname);
    if (isFile) {
      const url: string = host.concat(`/dashboard/company_${rowId}.json`);
      fetch(url).then((response: Response) => {
        response.blob().then((blob: Blob) => {
          const href: string = window.URL.createObjectURL(blob);
          const a: HTMLAnchorElement = document.createElement('a');
          a.href = href;
          a.download = `company_${rowId}.json`;
          a.click();
        }).catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        });
      }).catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      });
    }
  };

  public showFreeboard = (): void => {
    const { rowId } = this.props || {};
    const source: string = `${config.fbdRootUrl}#source=/dashboard/company_${rowId}.json`;
    window.open(source, `freeboard_company_${rowId}`, 'width=750,height=550,scrollbars=YES');
  }

  public render(): JSX.Element {
    const { data, loading } = this.state;
    const { fileId, fileName } = this;

    return (
      <div id="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Company {data?.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form
                        formValues={this.state}
                        onChange={this.handleChange}
                        onChangeTwo={this.handleChangeTwo}
                        onCloseModal={this.onCloseModal}
                        onInfo={this.onInfo}
                        onReset={this.onReset}
                        onHandleUploadFile={this.handleUploadFile}
                        onHandleDeletedFile={this.handleDeletedFile}
                        fileId={fileId}
                        fileName={fileName}
                        download={this.download}
                        showFreeboard={this.showFreeboard}
                      />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
